import Link from 'next/link';
import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { useSheetManager } from '@/lib/ui';
import { useBrowserNavigation } from '@/lib/ui/BrowserNavigationContext';
import type { SheetNavigationList, SheetNavigationItem } from '@/lib/ui/SheetManager';
import EntityDetailView from '@/components/EntityDetailView';

interface ShowEntityDetailOptions {
  keepOpenOnRouteChange?: boolean;
  title?: string;
  /** Navigation list for prev/next browsing through entities. When omitted, falls back to browser context. */
  navigationList?: SheetNavigationList;
  /**
   * Slug used to build the "Open full page" link. When omitted, the numeric id
   * is used instead. Prefer passing the slug for nicer URLs.
   */
  slug?: string;
  /**
   * Optional extra content rendered in the sheet header row, before the
   * "Open full page" link (e.g. per-caller actions).
   */
  headerExtra?: ReactNode;
}

/**
 * Resolve the standalone page route for an entity, mirroring EntityCard's
 * path logic: people live under `/people/`, everything else under `/entities/`.
 */
function entityRoutePath(entityType: string, identifier: string): string {
  switch (entityType) {
    case 'person':
      return `/people/${identifier}`;
    default:
      return `/entities/${identifier}`;
  }
}

export function useEntityDetailSheet() {
  const { openSheet } = useSheetManager();
  const { entityNavigationList } = useBrowserNavigation();

  const showEntityDetail = (
    entityId: number,
    entityType: string,
    options?: ShowEntityDetailOptions
  ) => {
    const navList = options?.navigationList ?? entityNavigationList;
    const routeId = options?.slug || String(entityId);
    openSheet({
      id: `entity-detail-${entityId}`,
      title: options?.title ?? 'Entity Details',
      headerExtra: (
        <div className="flex items-center gap-3">
          {options?.headerExtra}
          <Link
            href={entityRoutePath(entityType, routeId)}
            className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]"
          >
            <ExternalLink className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline">Open full page</span>
          </Link>
        </div>
      ),
      content: <EntityDetailView id={entityId.toString()} entityType={entityType} />,
      maxWidth: 'sm:max-w-4xl',
      keepOpenOnRouteChange: options?.keepOpenOnRouteChange ?? false,
      navigationList: navList,
      currentNavigationId: navList ? String(entityId) : undefined,
    });
  };

  return { showEntityDetail };
}

/** Helper to build a navigation list for entities */
export function buildEntityNavigationList(
  entities: Array<{ id: number; name_1?: string; type?: string; entityType?: string }>,
  entityType: string
): SheetNavigationList {
  return {
    items: entities.map((e) => ({
      id: String(e.id),
      title: e.name_1 || 'Entity Details',
    })),
    renderContent: (item: SheetNavigationItem) => {
      const entity = entities.find((e) => String(e.id) === item.id);
      const type = entity?.entityType || entity?.type || entityType;
      return <EntityDetailView id={item.id} entityType={type} />;
    },
    maxWidth: 'sm:max-w-4xl',
  };
}
